import type { ColumnGroup } from '@/components/table/table-core';
import {
  type ColumnAttributes,
  LockableField,
} from '@/components/table/table-types';
import type { TableColumn } from './simulation-types';

export const getColumnData = (
  lockedColumn: LockableField | undefined,
  isLel: boolean,
  isGroupedBy?: 'rm' | 'rrp',
  isGlobalPriceManager?: boolean,
): Record<TableColumn, ColumnAttributes> => {
  const isGrouped = Boolean(isGroupedBy);
  return {
    ...(isGrouped
      ? {
          productCount: {
            label: 'SKU Count',
            size: 100,
            alignment: 'right',
            aggregationFn: 'count',
          },
        }
      : {}),
    priceUnitId: {
      label: 'Price List',
      size: 100,
      alignment: 'left',
    },
    productId: {
      label: 'SKU ID',
      size: 100,
      alignment: 'left',
    },
    productName: {
      label: 'SKU Description',
      size: 150,
      alignment: 'left',
    },
    license: {
      label: 'License',
      size: 80,
      alignment: 'left',
    },
    themeName: {
      label: 'Theme',
      size: 120,
      alignment: 'left',
    },
    groupName: {
      label: 'Group',
      size: 120,
      hidden: !isLel,
      alignment: 'left',
    },
    productClassLevelOneName: {
      label: 'Product Class Lvl 1',
      size: 150,
      hidden: !isLel,
      alignment: 'left',
    },
    productClassLevelTwoName: {
      label: 'Product Class Lvl 2',
      size: 150,
      hidden: true,
      alignment: 'left',
    },
    productGroupName: {
      label: 'Product Group',
      size: 140,
      hidden: true,
      alignment: 'left',
    },
    productCategoryName: {
      label: 'Product Category',
      size: 140,
      hidden: true,
      alignment: 'left',
    },
    developmentOwnershipName: {
      label: 'Development Ownership',
      size: 170,
      hidden: true,
      alignment: 'left',
    },
    productCompositionName: {
      label: 'Product Composition',
      size: 160,
      hidden: true,
      alignment: 'left',
    },
    superSegmentName: {
      label: 'Super Segment',
      size: 130,
      hidden: true,
      alignment: 'left',
    },
    themeCategoryName: {
      label: 'Theme Category',
      size: 130,
      hidden: true,
      alignment: 'left',
    },
    isRareItem: {
      label: 'Rare Item',
      size: 100,
      hidden: true,
      alignment: 'left',
    },
    boxSizeName: {
      label: 'Box Size',
      size: 120,
      hidden: true,
      alignment: 'left',
    },
    businessToBusinessLaunchDate: {
      label: 'B2B Launch Date',
      size: 130,
      hidden: true,
      alignment: 'left',
    },
    localLaunchDate: {
      label: 'Local Launch Date',
      size: 130,
      hidden: true,
      alignment: 'left',
    },
    localExitDate: {
      label: 'Local Exit Date',
      size: 130,
      hidden: true,
      alignment: 'left',
    },
    directToConsumerLifelong: {
      label: 'D2C Lifelong',
      size: 120,
      hidden: true,
      alignment: 'left',
    },
    sizeDimension: {
      label: 'Size',
      size: 100,
      hidden: true,
      alignment: 'left',
    },
    pieceCount: {
      label: 'Piece Count',
      size: 110,
      hidden: true,
      alignment: 'right',
    },
    smartPlayName: {
      label: 'Smart Play',
      size: 120,
      hidden: true,
      alignment: 'left',
    },
    playTypeName: {
      label: 'Play Type',
      size: 140,
      hidden: true,
      alignment: 'left',
    },
    topThemeName: {
      label: 'Top Theme',
      size: 120,
      hidden: true,
      alignment: 'left',
    },
    productAudience: { label: 'Audience', size: 120, alignment: 'left' },
    primaryKey: { label: 'Key', size: 50, alignment: 'left', hidden: true },
    launchStatus: { label: 'Status', size: 70, alignment: 'left' },
    localLeadRrp: {
      label: 'LL RRP',
      size: isGrouped ? 150 : 100,
      alignment: 'right',
      aggregationFn: 'count',
    },
    localLeadRm: {
      label: 'LL RM%',
      size: isGrouped ? 150 : 100,
      alignment: 'right',
      aggregationFn: 'count',
    },
    localLeadLp: { label: 'LL LP', size: 100, alignment: 'right' },
    asIsRrp: {
      label: 'RRP',
      exportLabel: 'as-is RRP',
      size: 100,
      alignment: 'right',
    },
    asIsRm: {
      label: 'RM%',
      exportLabel: 'as-is RM%',
      size: 100,
      alignment: 'right',
    },
    asIsLp: {
      label: 'LP',
      exportLabel: 'as-is LP',
      size: 100,
      alignment: 'right',
    },
    rrp: {
      label: 'RRP',
      size: 100,
      columnType:
        lockedColumn !== LockableField.rrp && isGroupedBy !== 'rrp'
          ? 'editable'
          : undefined,
      alignment: 'right',
    },
    rm: {
      label: 'RM%',
      size: 100,
      columnType:
        lockedColumn !== LockableField.rm && isGroupedBy !== 'rm'
          ? 'editable'
          : undefined,
      alignment: 'right',
    },
    lp: {
      label: 'LP',
      size: 100,
      columnType: lockedColumn !== LockableField.lp ? 'editable' : undefined,
      alignment: 'right',
    },
    priceStatus: {
      label: 'Price Validity',
      size: 85,
      hidden: true,
      alignment: 'right',
    },
    rrpIndexLocalLead: {
      label: 'LL RRP Index',
      size: 100,
      alignment: 'right',
    },
    ppcMark: {
      label: 'PPC Mark',
      size: 80,
      alignment: 'left',
    },
    lpIndexLocalLead: { label: 'LL LP Index', size: 100, alignment: 'right' },
    lpDelta: { label: 'LP Δ %', size: 90, alignment: 'right' },
    nip: { label: 'NIP', size: 100, alignment: 'right', hidden: true },
    llNip: { label: 'LL NIP', size: 100, alignment: 'right', hidden: true },
    llNipIndex: {
      label: 'LL NIP Index',
      size: 100,
      alignment: 'right',
      hidden: true,
    },
    vat: { label: 'VAT %', size: 80, alignment: 'right', hidden: true },
    hasPriceChange: {
      label: 'Price Δ',
      size: 100,
      alignment: 'right',
      hidden: true,
    },
    listPriceFmcFactor: {
      label: 'LP/FMC Factor',
      size: 100,
      alignment: 'right',
    },
    hasFutureLpChange: {
      label: 'Future LP Δ',
      size: 80,
      alignment: 'right',
    },
    fmcLocal: {
      label: 'FMC',
      size: 100,
      alignment: 'right',
    },
    forecastedDemand: {
      label: 'SI Pcs',
      size: 100,
      alignment: 'right',
    },
    valueBasedPrice: {
      label: 'VBP',
      size: 100,
      alignment: 'right',
      hidden: true,
      excluded: !isGlobalPriceManager,
    },
    comment: {
      label: 'Comment',
      size: 150,
      alignment: 'left',
      columnType: 'editable',
    },
  } as Record<TableColumn, ColumnAttributes>;
};

export const getColumnGroups = (
  isGroupedBy?: 'rm' | 'rrp',
): ColumnGroup<TableColumn>[] => [
  {
    id: 'price-list',
    header: 'Price List',
    columnCategory: 'priceList',
    columnKeys: ['priceUnitId'],
  },
  {
    id: 'product-data-pinned',
    header: 'Product Data',
    columnCategory: 'productData',
    columnKeys: isGroupedBy
      ? ['productId', 'productName', 'productCount']
      : ['productId', 'productName'],
  },
  {
    id: 'product-data',
    header: 'Product Data',
    columnCategory: 'productData',
    columnKeys: [
      'license',
      'themeName',
      'groupName',
      'productClassLevelOneName',
      'productClassLevelTwoName',
      'productGroupName',
      'productCategoryName',
      'developmentOwnershipName',
      'productCompositionName',
      'superSegmentName',
      'themeCategoryName',
      'isRareItem',
      'boxSizeName',
      'businessToBusinessLaunchDate',
      'localLaunchDate',
      'localExitDate',
      'directToConsumerLifelong',
      'sizeDimension',
      'pieceCount',
      'smartPlayName',
      'playTypeName',
      'topThemeName',
      'productAudience',
      'primaryKey',
      'launchStatus',
    ],
  },
  {
    id: 'local-lead',
    header: 'Local Lead',
    columnCategory: 'localLead',
    columnKeys: ['localLeadRrp', 'localLeadRm', 'localLeadLp'],
  },
  {
    id: 'previous-year',
    header: 'Local as-is',
    columnCategory: 'localAsIs',
    columnKeys: ['asIsRrp', 'asIsRm', 'asIsLp'],
  },
  {
    id: 'pricing-simulation',
    header: 'Pricing simulation',
    columnCategory: 'pricingSimulation',
    columnKeys: ['rrp', 'rm', 'lp'],
  },
  {
    id: 'simulation-result',
    header: 'Simulation result',
    columnCategory: 'simulationResult',
    columnKeys: [
      'priceStatus',
      'rrpIndexLocalLead',
      'ppcMark',
      'lpIndexLocalLead',
      'lpDelta',
      'nip',
      'llNip',
      'llNipIndex',
      'vat',
      'hasPriceChange',
      'listPriceFmcFactor',
      'hasFutureLpChange',
    ],
  },
  {
    id: 'other',
    header: 'Other',
    columnCategory: 'other',
    columnKeys: ['fmcLocal', 'forecastedDemand', 'valueBasedPrice', 'comment'],
  },
];
